import { ISimulation, ISimulationScore } from '@/types';
import { useCallback } from 'react';

/**
 * Input contract for `useFindSimulationScorecard`.
 */
interface UseFindSimulationScorecardParams {
  /**
   * Navigation direction:
   * - `next` moves forward in the ordered score list
   * - `previous` moves backward in the ordered score list
   */
  direction: 'next' | 'previous';

  /**
   * The currently active scorecard used as the navigation anchor.
   */
  score: ISimulationScore | null;

  /**
   * The complete score collection available in the current context.
   */
  scores: ISimulationScore[] | null;

  /**
   * The simulation whose `history` determines which scores are eligible
   * for navigation.
   */
  simulation: ISimulation | null;
}

/**
 * Returns the adjacent scorecard for a simulation, based on `direction`.
 *
 * High-level behavior:
 * 1. Validate required inputs (`score`, `scores`, and `simulation.history`).
 * 2. Resolve simulation-specific scorecards from `simulation.history`.
 * 3. Sort resolved scorecards by `dateCompleted` (ascending chronology).
 * 4. Locate the current score index.
 * 5. Compute next/previous index with circular wrap-around.
 * 6. Return the resolved scorecard.
 *
 * Note:
 * This file intentionally preserves existing runtime behavior.
 */
export function useFindSimulationScorecard({
  direction = 'next',
  score,
  scores,
  simulation,
}: UseFindSimulationScorecardParams): ISimulationScore | null {
  // ================================
  // Memoized resolver callback
  // ================================
  /**
   * Computes the target scorecard for the requested direction.
   * The function is memoized so downstream consumers can keep stable
   * references when dependencies do not change.
   */
  const findScoreCard = useCallback(() => {
    // ================================
    // Guard clauses
    // ================================
    // Eject if necessary data is missing
    if (!score || score === null) return null;
    if (!scores || scores.length === 0) return null;
    if (!simulation || !simulation.history) return null;

    // ================================
    // Build simulation score list
    // ================================
    /**
     * Convert `simulation.history` IDs into actual score objects by looking
     * them up from the `scores` array.
     *
     * Any missing IDs are filtered out.
     *
     * The resulting list is sorted chronologically by completion date so
     * navigation semantics remain deterministic.
     */
    const simScores = simulation.history
      .map((scoreId) => scores.find((score) => score.uuid === scoreId))
      .filter((score): score is ISimulationScore => !!score)
      .sort((a, b) => new Date(a.dateCompleted).getTime() - new Date(b.dateCompleted).getTime());

    // If no scorecards resolve from history, there is no adjacent score to return.
    if (simScores.length === 0) {
      return null;
    }

    // ================================
    // Locate current position
    // ================================
    /**
     * Find the index of the current score in the ordered list.
     *
     * If the current score is not present, `findIndex` returns `-1`.
     * Existing behavior for that case is intentionally preserved.
     */
    const currentIndex = simScores.findIndex((s) => s.uuid === score.uuid);

    // If the current score is not part of this simulation history, navigation
    // cannot be resolved deterministically.
    if (currentIndex === -1) {
      return null;
    }

    // ================================
    // Circular index calculation
    // ================================
    /**
     * Compute next/previous positions with modulo arithmetic so navigation
     * wraps around at both ends of the list.
     */
    const nextIndex = (currentIndex + 1) % simScores.length;
    const previousIndex = (currentIndex - 1 + simScores.length) % simScores.length;

    // ================================
    // Resolve target scorecard
    // ================================
    return direction === 'next' ? simScores[nextIndex] : simScores[previousIndex];
  }, [direction, score, scores, simulation]);

  // ================================
  // Execute the memoized resolver and
  // return the selected scorecard.
  // ================================
  return findScoreCard();
}
